import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, updateDoc, deleteDoc, addDoc, getDoc, writeBatch } from 'firebase/firestore';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, Clock, User, RotateCcw, Calendar, Check, Loader, AlertCircle, Shield } from 'lucide-react';
import ModernModal from './ModernModal';
import StandardAddAppointmentModal from './StandardAddAppointmentModal';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import RecurrenceMoveModal from './RecurrenceMoveModal';
import AppointmentViewModal from './AppointmentViewModal';
import EnhancedAppointmentDetailsModal from './EnhancedAppointmentDetailsModal';
import EnhancedEditAppointmentModal from './EnhancedEditAppointmentModal';
import { useTheme } from '../contexts/ThemeContext';
import '../DayView.css';

// Fonction utilitaire pour obtenir une couleur de contraste (noir ou blanc)
const getContrastColor = (hexColor) => {
  // Convertir la couleur hex en RGB
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);

  // Calculer la luminance relative
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Retourner noir ou blanc selon la luminance
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Fonction utilitaire pour comparer deux dates
const isSameDay = (date1, date2) => {
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();
  }
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

// Composant pour un rendez-vous draggable
const SortableAppointment = ({ appointment, position, getStatusClass, formatTime, handleAppointmentClick, handleAppointmentDoubleClick, canEditAppointments, isMobile }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: appointment.id,
    disabled: !canEditAppointments
  });

  const style = {
    left: `${position.left}%`,
    width: `${position.width}%`,
    position: 'absolute',
    top: '50%',
    transform: CSS.Transform.toString(transform) || 'translateY(-50%)',
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: canEditAppointments ? 'grab' : 'default',
  };

  // Utiliser la couleur du bénéficiaire, mais griser les rendez-vous terminés
  const baseColor = appointment.clientColor || '#808080';
  const backgroundColor = appointment.status === 'completed' ? '#9ca3af' : baseColor;
  // Améliorer la visibilité du texte avec des couleurs plus contrastées
  const textColor = getContrastColor(backgroundColor);
  // Forcer une couleur de texte plus visible si nécessaire
  const finalTextColor = textColor === '#000000' ? '#1f2937' : '#f9fafb';

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        zIndex: isDragging ? 1000 : 1,
        border: canEditAppointments ? '2px solid #3b82f6' : 'none',
        backgroundColor: backgroundColor,
        color: finalTextColor,
        borderColor: backgroundColor,
      }}
      {...attributes}
      {...listeners}
      className={`dv-timeline-appointment ${getStatusClass(appointment.status)} ${isDragging ? 'dragging' : ''} ${canEditAppointments ? 'dv-draggable' : ''}`}
      onClick={(e) => {
        if (!isDragging) {
          handleAppointmentClick(appointment, e);
        }
      }}
      onDoubleClick={(e) => {
        if (!isDragging) {
          handleAppointmentDoubleClick(appointment);
        }
      }}
    >
      <div className="dv-appointment-content">
        {/* En-tête avec nom du bénéficiaire */}
        <div className="dv-appointment-header">
          <div className="dv-appointment-client">
            <User size={10} className="dv-client-icon" />
            <span className="dv-client-name">
              {appointment.clientName || 'Bénéficiaire non spécifié'}
            </span>
          </div>
          {appointment.status === 'completed' && (
            <div className="dv-completed-indicator">
              <Check size={10} />
            </div>
          )}
        </div>

        {/* Heure du rendez-vous */}
        <div className="dv-appointment-time">
          <Clock size={8} className="dv-time-icon" />
          {`${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
        </div>

        {/* Statut du rendez-vous */}
        {appointment.status && appointment.status !== 'assigne' && (
          <div className="dv-status-indicator">
            {appointment.status === 'libre' && '⚠️ Libre'}
            {appointment.status === 'reserve' && '⏳ En attente'}
            {appointment.status === 'in_progress' && '▶️ En cours'}
            {appointment.status === 'completed' && '✅ Terminé'}
          </div>
        )}

        {/* Indicateur de notes */}
        {appointment.notes && appointment.notes.content && (
          <div className="dv-notes-indicator">
            📝
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour une zone droppable
const DroppableRow = ({ id, children, className }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'dv-dragging-over' : ''}`}
    >
      {children}
    </div>
  );
};

const DayView = ({ db, currentDate, currentUser, showCompletedAppointments, onToggleCompletedAppointments, filterText }) => {
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEditAppointment, setSelectedEditAppointment] = useState(null);
  const [selectedDetailAppointment, setSelectedDetailAppointment] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedUnassignedAppointment, setSelectedUnassignedAppointment] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInfoAppointment, setSelectedInfoAppointment] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [settings, setSettings] = useState({
    planningOpeningTime: '08:00',
    planningClosingTime: '18:00',
    showWeekends: true,
    firstDayOfWeek: 1,
  });
  const [showRecurrenceMoveModal, setShowRecurrenceMoveModal] = useState(false);
  const [pendingMoveData, setPendingMoveData] = useState(null);
  const [showRecurrenceUpdateModal, setShowRecurrenceUpdateModal] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [showRecurrenceDeleteModal, setShowRecurrenceDeleteModal] = useState(false);
  const [pendingDeleteData, setPendingDeleteData] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [openInEditMode, setOpenInEditMode] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
    create: false,
    delete: false,
    manage: false
  });
  const [showMoveConfirmationModal, setShowMoveConfirmationModal] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);

  const { theme } = useTheme();

  // Gestion de la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Récupération des permissions utilisateur
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;

        // Essayer d'abord le nouveau système advanced_roles
        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const roleData = advancedRoleDoc.data();
            const appointmentsPerms = roleData.permissions?.appointments || {};
            setUserPermissions({
              read: appointmentsPerms.read || false,
              write: appointmentsPerms.write || false,
              create: appointmentsPerms.create || false,
              delete: appointmentsPerms.delete || false,
              manage: appointmentsPerms.manage || false
            });
            return;
          }
        } catch (advancedError) {
          console.log("Nouveau système de permissions non disponible, utilisation du fallback...");
        }

        // Fallback: ancien système
        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          setUserPermissions({
            read: roleData.permissions.includes('read_appointments'),
            write: roleData.permissions.includes('write_appointments'),
            create: roleData.permissions.includes('write_appointments'),
            delete: roleData.permissions.includes('write_appointments'),
            manage: roleData.permissions.includes('write_appointments')
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des permissions de l'utilisateur:", error);
    }
  }, [db, currentUser]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  const canEditAppointments = userPermissions.write;
  // Seuls les utilisateurs avec droits de gestion peuvent voir tous les planning
  const canViewAllAppointments = userPermissions.manage && !isMobile;
  // Les utilisateurs avec droits de création/modification d'intervenants peuvent voir tous les planning
  const canManageEmployees = userPermissions.manage;

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
        tolerance: 5,
      },
    })
  );

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('isArchived', '==', false));

      // Promesses parallèles : liste filtrée ET utilisateur courant spécifique
      const [querySnapshot, currentUserDoc] = await Promise.all([
        getDocs(q),
        currentUser ? getDoc(doc(db, 'employees', currentUser.userId)) : Promise.resolve(null)
      ]);

      // Map des employés filtrés
      const fetchedEmployeesMap = new Map();
      querySnapshot.docs.forEach(doc => {
        fetchedEmployeesMap.set(doc.id, {
          userId: doc.id,
          ...doc.data(),
          color: doc.data().color || '#808080',
          photoURL: doc.data().photoURL || null,
          isAdmin: false
        });
      });

      // AJOUT FORCE DE L'UTILISATEUR COURANT s'il existe et n'est pas déjà présent
      if (currentUserDoc && currentUserDoc.exists()) {
        const userData = currentUserDoc.data();
        // On l'ajoute ou on l'écrase pour être sûr de l'avoir
        fetchedEmployeesMap.set(currentUserDoc.id, {
          userId: currentUserDoc.id,
          ...userData,
          color: userData.color || '#808080',
          photoURL: userData.photoURL || null,
          isAdmin: false
        });
      }

      const fetchedEmployees = Array.from(fetchedEmployeesMap.values());

      // Récupérer les rôles pour chaque employé
      const employeesWithRoles = await Promise.all(
        fetchedEmployees.map(async (employee) => {
          try {
            let roleName = 'Non défini';
            let roleColor = '#808080';

            if (employee.role) {
              // Essayer d'abord le nouveau système advanced_roles
              try {
                const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', employee.role));
                if (advancedRoleDoc.exists()) {
                  const roleData = advancedRoleDoc.data();
                  roleName = roleData.name || 'Rôle avancé';
                  roleColor = roleData.color || '#808080';
                } else {
                  // Fallback: ancien système de rôles
                  const roleDoc = await getDoc(doc(db, 'roles', employee.role));
                  if (roleDoc.exists()) {
                    const roleData = roleDoc.data();
                    roleName = roleData.name || 'Rôle standard';
                    roleColor = roleData.color || '#808080';
                  }
                }
              } catch (roleError) {
                console.warn(`Erreur lors de la récupération du rôle pour ${employee.userId}:`, roleError);
              }
            }

            return {
              ...employee,
              roleName,
              roleColor
            };
          } catch (error) {
            console.error(`Erreur lors du traitement de l'employé ${employee.userId}:`, error);
            return {
              ...employee,
              roleName: 'Erreur',
              roleColor: '#ff0000'
            };
          }
        })
      );

      // Filtrer pour ne garder que les intervenants actifs
      console.log("DayView - Filtering employees. Total fetched:", employeesWithRoles.length);
      const filteredEmployees = employeesWithRoles.filter(employee => {
        // Toujours inclure l'utilisateur courant s'il est dans la liste (sauf s'il est explicitement archivé, mais la requête filtre déjà les archivés)
        if (currentUser && employee.userId === currentUser.userId) {
          console.log("DayView - FORCE KEEP CurrentUser:", employee.name);
          return true;
        }

        if (employee.role === 'beneficiaire') {
          return false;
        }

        const isActive = employee.isActive !== false && !employee.isArchived;
        return isActive;
      });
      console.log("DayView - Filtered count:", filteredEmployees.length);

      setEmployees(filteredEmployees);
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db, userPermissions.manage, currentUser]);

  const fetchAppointments = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay))
      );
      const querySnapshot = await getDocs(q);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));

      // Récupérer les informations des clients
      const appointmentsWithClientInfo = await Promise.all(fetchedAppointments.map(async appointment => {
        const clientDoc = await getDoc(doc(db, 'clients', appointment.clientId));
        const clientData = clientDoc.data();
        return {
          ...appointment,
          clientName: clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Bénéficiaire non spécifié',
          clientColor: clientData ? clientData.color : '#808080',
          status: appointment.status || 'libre'
        };
      }));

      // Filtrer les rendez-vous en fonction des permissions
      const filteredAppointments = appointmentsWithClientInfo.filter(app =>
        canViewAllAppointments ||
        app.employeeId === currentUser.userId ||
        (userPermissions.read && (app.status === 'libre' || app.status === 'reserve')) ||
        app.status === 'in_progress' || app.status === 'completed'
      );

      setAppointments(filteredAppointments);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    }
  }, [db, currentDate, currentUser, canViewAllAppointments, userPermissions.read]);

  // Fonction pour récupérer les clients
  const fetchClients = useCallback(async () => {
    if (!db) return;
    try {
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('isActive', '==', true));
      const clientsSnapshot = await getDocs(q);
      const fetchedClients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClients(fetchedClients);
    } catch (error) {
      console.error("Erreur lors de la récupération des clients:", error);
    }
  }, [db]);

  useEffect(() => {
    fetchEmployees();
    fetchAppointments();
    fetchClients();
  }, [fetchEmployees, fetchAppointments, fetchClients, refreshTrigger]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!db) return;

      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          planningOpeningTime: data.planningOpeningTime || '08:00',
          planningClosingTime: data.planningClosingTime || '18:00',
          showWeekends: data.showWeekends !== false,
          firstDayOfWeek: data.firstDayOfWeek || 1,
        });
      }
    };
    fetchSettings();
  }, [db]);

  const fetchApprovedLeaves = useCallback(async () => {
    if (!db) return;
    try {
      const leavesRef = collection(db, 'leaves');
      const q = query(
        leavesRef,
        where('status', '==', 'approved')
      );
      const querySnapshot = await getDocs(q);
      const leaves = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        startDate: doc.data().startDate.toDate(),
        endDate: doc.data().endDate.toDate()
      }));
      setApprovedLeaves(leaves);
    } catch (error) {
      console.error("Erreur lors de la récupération des congés:", error);
    }
  }, [db]);

  useEffect(() => {
    fetchApprovedLeaves();
  }, [fetchApprovedLeaves]);

  const isEmployeeOnLeave = useCallback((employeeId) => {
    if (!approvedLeaves.length) return false;

    const today = new Date(currentDate);
    today.setHours(0, 0, 0, 0);

    return approvedLeaves.some(leave => {
      const startDate = new Date(leave.startDate);
      const endDate = new Date(leave.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return leave.employeeId === employeeId &&
        today >= startDate &&
        today <= endDate;
    });
  }, [approvedLeaves, currentDate]);

  const handleAppointmentClick = (appointment, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (clickTimeout) {
      // Double-clic détecté
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      handleAppointmentDoubleClick(appointment);
    } else {
      // Définir un timer pour le simple clic
      const timer = setTimeout(() => {
        handleSingleClick(appointment);
        setClickTimeout(null);
      }, 200);
      setClickTimeout(timer);
    }
  };

  const handleSingleClick = (appointment) => {
    if (appointment.status === 'libre') {
      setSelectedUnassignedAppointment(appointment);
      setShowBookingModal(true);
    } else if (appointment.status === 'reserve') {
      setSelectedInfoAppointment(appointment);
      setShowInfoModal(true);
    } else {
      setSelectedDetailAppointment(appointment);
      setShowDetailsModal(true);
    }
  };

  const handleAppointmentDoubleClick = (appointment) => {
    // Vérifier les permissions avant d'autoriser l'édition
    if (!canEditAppointments) {
      console.log("❌ Utilisateur non autorisé à modifier les rendez-vous");
      return;
    }

    // Vérifier si l'utilisateur peut modifier ce rendez-vous spécifique
    const canEditThisAppointment =
      // L'utilisateur peut modifier ses propres rendez-vous
      appointment.employeeId === currentUser.userId ||
      // Ou l'utilisateur a des droits de gestion
      userPermissions.manage ||
      // Ou l'utilisateur peut modifier tous les rendez-vous
      canViewAllAppointments;

    if (!canEditThisAppointment) {
      console.log("❌ Utilisateur non autorisé à modifier ce rendez-vous spécifique");
      return;
    }

    setSelectedEditAppointment(appointment);
    setShowEditModal(true);
  };

  const handleAddAppointment = async (appointmentData) => {
    if (!canEditAppointments) return;
    try {
      const newAppointmentRef = doc(collection(db, 'appointments'));
      await addDoc(collection(db, 'appointments'), {
        ...appointmentData,
        date: Timestamp.fromDate(currentDate),
        status: appointmentData.employeeId ? 'assigne' : 'libre'
      });

      // Note: Cette fonction n'est pas utilisée actuellement car le bouton d'ajout a été retiré
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Erreur lors de l'ajout du rendez-vous:", error);
    }
  };

  const handleUpdateAppointment = async (updatedAppointment, scope = 'this') => {
    if (!canEditAppointments) return;

    // Si le rendez-vous est récurrent et qu'on n'a pas encore demandé le scope
    if (updatedAppointment.recurrenceSeriesId && scope === 'this' && !pendingUpdateData) {
      // Stocker les données et afficher le modal de choix
      setPendingUpdateData(updatedAppointment);
      setShowRecurrenceUpdateModal(true);
      return;
    }

    try {
      if (scope === 'this') {
        // Cette occurrence uniquement
        const appointmentRef = doc(db, 'appointments', updatedAppointment.id);

        let employeeName = '';
        if (updatedAppointment.employeeId) {
          const employee = employees.find(emp => emp.userId === updatedAppointment.employeeId);
          if (employee) {
            employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
          }
        }

        const status = updatedAppointment.status || (updatedAppointment.employeeId ? 'assigne' : 'libre');

        await updateDoc(appointmentRef, {
          ...updatedAppointment,
          status: status,
          employeeName: employeeName
        });

      } else if (scope === 'future') {
        // Toutes les occurrences futures
        const recurrenceQuery = query(
          collection(db, 'appointments'),
          where('recurrenceSeriesId', '==', updatedAppointment.recurrenceSeriesId)
        );
        const recurrenceSnapshot = await getDocs(recurrenceQuery);

        const currentDate = new Date(updatedAppointment.date);
        currentDate.setHours(0, 0, 0, 0);

        const appointmentsToUpdate = recurrenceSnapshot.docs.filter(docSnap => {
          const appDate = new Date(docSnap.data().date.toDate());
          appDate.setHours(0, 0, 0, 0);
          return appDate >= currentDate;
        });

        for (const docSnap of appointmentsToUpdate) {
          let employeeName = '';
          if (updatedAppointment.employeeId) {
            const employee = employees.find(emp => emp.userId === updatedAppointment.employeeId);
            if (employee) {
              employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
            }
          }

          const status = updatedAppointment.status || (updatedAppointment.employeeId ? 'assigne' : 'libre');

          await updateDoc(doc(db, 'appointments', docSnap.id), {
            ...updatedAppointment,
            id: docSnap.id,
            date: docSnap.data().date,
            startTime: updatedAppointment.startTime,
            endTime: updatedAppointment.endTime,
            employeeId: updatedAppointment.employeeId,
            status: status,
            employeeName: employeeName
          });
        }
      }

      setShowEditModal(false);
      setShowRecurrenceUpdateModal(false);
      setPendingUpdateData(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rendez-vous:", error);
    }
  };

  const handleDeleteAppointment = async (appointmentId, scope = 'this') => {
    if (!canEditAppointments) return;

    try {
      // Récupérer les données du rendez-vous
      const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));
      const appointmentData = appointmentDoc.data();

      // Si le rendez-vous est récurrent et qu'on n'a pas encore demandé le scope
      if (appointmentData.recurrenceSeriesId && scope === 'this' && !pendingDeleteData) {
        // Stocker l'ID et afficher le modal de choix
        setPendingDeleteData({ id: appointmentId, ...appointmentData });
        setShowRecurrenceDeleteModal(true);
        return;
      }

      if (scope === 'this') {
        // Cette occurrence uniquement
        await deleteDoc(doc(db, 'appointments', appointmentId));
      } else if (scope === 'future') {
        // Toutes les occurrences futures
        const recurrenceQuery = query(
          collection(db, 'appointments'),
          where('recurrenceSeriesId', '==', appointmentData.recurrenceSeriesId)
        );
        const recurrenceSnapshot = await getDocs(recurrenceQuery);

        const currentDate = new Date(appointmentData.date.toDate());
        currentDate.setHours(0, 0, 0, 0);

        const appointmentsToDelete = recurrenceSnapshot.docs.filter(docSnap => {
          const appDate = new Date(docSnap.data().date.toDate());
          appDate.setHours(0, 0, 0, 0);
          return appDate >= currentDate;
        });

        // Utiliser batch pour supprimer tous les rendez-vous
        const batch = writeBatch(db);
        appointmentsToDelete.forEach(docSnap => {
          batch.delete(doc(db, 'appointments', docSnap.id));
        });
        await batch.commit();
      }

      setShowEditModal(false);
      setShowRecurrenceDeleteModal(false);
      setPendingDeleteData(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
    }
  };

  // Fonctions pour la gestion des réservations (comme DayViewMobile)
  const handleBookAppointment = async () => {
    if (!selectedUnassignedAppointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedUnassignedAppointment.id);
      await updateDoc(appointmentRef, {
        employeeId: currentUser.userId,
        status: 'reserve'
      });

      fetchAppointments();
      setShowBookingModal(false);
    } catch (error) {
      console.error("Erreur lors de la réservation du rendez-vous:", error);
    }
  };

  // Fonction pour approuver un rendez-vous en attente
  const handleApproveAppointment = async () => {
    if (!selectedInfoAppointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'assigne'
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'approbation du rendez-vous:", error);
    }
  };

  // Fonction pour refuser un rendez-vous en attente
  const handleRejectAppointment = async () => {
    if (!selectedInfoAppointment) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors du refus du rendez-vous:", error);
    }
  };

  // Fonction pour annuler sa propre réservation
  const handleCancelReservation = async () => {
    if (!selectedInfoAppointment || selectedInfoAppointment.employeeId !== currentUser.userId) return;

    try {
      const appointmentRef = doc(db, 'appointments', selectedInfoAppointment.id);
      await updateDoc(appointmentRef, {
        status: 'libre',
        employeeId: null
      });

      fetchAppointments();
      setShowInfoModal(false);
    } catch (error) {
      console.error("Erreur lors de l'annulation de la réservation:", error);
    }
  };

  // Fonction pour obtenir le nom d'un employé
  const getEmployeeName = useCallback((employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee ? (employee.name || `${employee.firstName} ${employee.lastName}`) : 'Employé non trouvé';
  }, [employees]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragOver = useCallback((event) => {
    // Logique de drag over si nécessaire
  }, []);

  // Fonction utilitaire pour convertir un temps en minutes
  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleDragEnd = useCallback((event) => {
    setActiveId(null);

    if (!canEditAppointments) {
      return;
    }

    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    const appointment = appointments.find(app => app.id === active.id);
    if (!appointment) {
      return;
    }

    const newEmployeeId = over.id === 'unassigned' ? null : over.id;
    const newStatus = over.id === 'unassigned' ? 'libre' : 'assigne';

    // Vérifier si l'employé est en congé
    if (newEmployeeId && isEmployeeOnLeave(newEmployeeId)) {
      console.log('❌ Impossible d\'assigner à un employé en congé');
      return;
    }

    // Validation des conflits horaires
    if (newEmployeeId) {
      const hasConflict = appointments.some(existingApp =>
        existingApp.employeeId === newEmployeeId &&
        existingApp.id !== appointment.id &&
        timeToMinutes(existingApp.startTime) < timeToMinutes(appointment.endTime) &&
        timeToMinutes(existingApp.endTime) > timeToMinutes(appointment.startTime)
      );

      if (hasConflict) {
        console.log('❌ Conflit horaire détecté avec le nouvel intervenant');
        return;
      }
    }

    // Trouver le nom de l'employé de destination
    let destinationName = 'Plages à combler';
    if (newEmployeeId) {
      const employee = employees.find(emp => emp.userId === newEmployeeId);
      if (employee) {
        destinationName = employee.name || `${employee.firstName} ${employee.lastName}`;
      }
    }

    // Stocker les données du déplacement en attente et afficher la modal de confirmation
    setPendingMove({
      appointment,
      newEmployeeId,
      newStatus,
      destinationName
    });
    setShowMoveConfirmationModal(true);
  }, [appointments, canEditAppointments, employees, isEmployeeOnLeave]);

  // Fonction pour confirmer le déplacement
  const confirmMoveAppointment = useCallback(async () => {
    if (!pendingMove) return;

    const { appointment, newEmployeeId, newStatus } = pendingMove;

    // S'assurer que le statut est correctement défini
    const finalStatus = newEmployeeId ? 'assigne' : 'libre';

    // Trouver le nom de l'employé
    let employeeName = '';
    if (newEmployeeId) {
      const employee = employees.find(emp => emp.userId === newEmployeeId);
      if (employee) {
        employeeName = employee.name || `${employee.firstName} ${employee.lastName}`;
      }
    }

    try {
      await updateDoc(doc(db, 'appointments', appointment.id), {
        employeeId: newEmployeeId,
        status: finalStatus,
        employeeName: employeeName
      });

      // Mettre à jour l'état local
      setAppointments(prevAppointments =>
        prevAppointments.map(app =>
          app.id === appointment.id
            ? {
              ...app,
              employeeId: newEmployeeId,
              status: finalStatus,
              employeeName: employeeName
            }
            : app
        )
      );

      // Trigger a refresh
      setRefreshTrigger(prev => prev + 1);
      console.log('✅ Rendez-vous déplacé avec succès vers:', newEmployeeId || 'non assigné', 'avec statut:', finalStatus);
    } catch (error) {
      console.error("❌ Erreur lors de la manipulation du rendez-vous:", error);
    }

    // Fermer la modal
    setShowMoveConfirmationModal(false);
    setPendingMove(null);
  }, [pendingMove, db, employees, setRefreshTrigger]);

  // Fonction pour annuler le déplacement
  const cancelMoveAppointment = useCallback(() => {
    setShowMoveConfirmationModal(false);
    setPendingMove(null);
  }, []);

  // Fonction pour générer les créneaux horaires de la journée
  const generateDayTimeline = () => {
    const slots = [];
    let currentTime = new Date(`2000-01-01T${settings.planningOpeningTime}`);
    const endTime = new Date(`2000-01-01T${settings.planningClosingTime}`);

    while (currentTime <= endTime) {
      slots.push(currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
      currentTime.setMinutes(currentTime.getMinutes() + 30); // Créneaux de 30 minutes
    }

    return slots;
  };

  const dayTimeline = generateDayTimeline();

  const openingMinutes = timeToMinutes(settings.planningOpeningTime);
  const closingMinutes = timeToMinutes(settings.planningClosingTime);
  const totalMinutes = closingMinutes - openingMinutes;

  // Fonction pour calculer la position et la largeur d'un rendez-vous dans la timeline horizontale
  const getAppointmentPosition = (appointment) => {
    const startMinutes = timeToMinutes(appointment.startTime);
    const endMinutes = timeToMinutes(appointment.endTime);

    // Calculer le nombre de créneaux (de 30 minutes)
    const totalSlots = dayTimeline.length;

    // Calculer la position et la largeur en termes de créneaux
    const minutesFromStart = startMinutes - openingMinutes;
    const durationMinutes = endMinutes - startMinutes;

    // Convertir en index de créneau (chaque créneau = 30 minutes)
    const startSlot = minutesFromStart / 30;
    const durationInSlots = durationMinutes / 30;

    // Convertir en pourcentage basé sur le nombre de créneaux
    const left = (startSlot / totalSlots) * 100;
    const width = (durationInSlots / totalSlots) * 100;

    // S'assurer que les valeurs sont dans les limites valides
    const finalLeft = Math.max(0, Math.min(left, 100));
    const finalWidth = Math.max(0, Math.min(width, 100 - finalLeft));

    return {
      left: finalLeft,
      width: finalWidth
    };
  };

  // Fonction pour obtenir l'initiale d'un nom
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getStatusClass = (status) => {
    // On utilise maintenant la couleur du bénéficiaire, donc on retourne juste une classe de base
    return 'dv-appointment-base';
  };

  const formatTime = (time) => {
    return time; // Format déjà correct
  };

  // Obtenir les employés visibles en fonction des permissions
  const visibleEmployees = useMemo(() => {
    if (!currentUser || !currentUser.userId) {
      return [];
    }
    // Les utilisateurs avec droits de gestion peuvent voir tous les planning
    if (canManageEmployees) {
      return employees;
    } else {
      // Les autres utilisateurs ne voient que leur propre planning
      return employees.filter(emp => emp.userId === currentUser.userId);
    }
  }, [employees, canManageEmployees, currentUser]);

  // Filtrer les rendez-vous selon showCompletedAppointments ET filterText
  const filteredAppointments = useMemo(() => {
    return appointments.filter(appointment => {
      // 1. Filtre Terminés
      const isCompleted = appointment.status === 'completed';
      const shouldShow = showCompletedAppointments || !isCompleted;

      if (!shouldShow) return false;

      // 2. Filtre Recherche Globale
      if (filterText) {
        const lowerFilter = filterText.toLowerCase();
        const clientName = appointment.clientName ? appointment.clientName.toLowerCase() : '';

        let employeeName = '';
        if (appointment.employeeId) {
          const emp = employees.find(e => e.userId === appointment.employeeId);
          if (emp) {
            employeeName = (emp.name || `${emp.firstName} ${emp.lastName}` || '').toLowerCase();
          }
        }

        return clientName.includes(lowerFilter) || employeeName.includes(lowerFilter);
      }

      return true;
    });
  }, [appointments, showCompletedAppointments, filterText, employees]);

  // Obtenir les rendez-vous non assignés (filtrés)
  const unassignedAppointments = filteredAppointments.filter(app => !app.employeeId || app.status === 'libre' || app.status === 'reserve');

  // Obtenir tous les IDs pour le contexte de drag
  const allAppointmentIds = appointments.map(app => app.id);

  // Récupérer le rendez-vous actif pour le DragOverlay
  const activeAppointment = activeId ? appointments.find(app => app.id === activeId) : null;

  // Fonction pour obtenir les rendez-vous d'un employé spécifique (filtrés)
  const getEmployeeAppointments = (employeeId) => {
    return filteredAppointments.filter(app => app.employeeId === employeeId);
  };

  // Vérifier si currentUser est null et retourner un message d'attente
  if (!currentUser || !currentUser.userId) {
    return (
      <div className="dv-container">
        <div className="text-center text-gray-500 py-8">
          <p>Chargement des informations utilisateur...</p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={allAppointmentIds} strategy={verticalListSortingStrategy}>
        <div className="dv-container">
          {/* En-tête avec la timeline */}
          <div className="dv-timeline-header">
            <div className="dv-employee-header">
              <span className="dv-header-title">Intervenants</span>
            </div>
            <div className="dv-time-header">
              {dayTimeline.map((time, index) => (
                <div key={index} className="dv-time-slot">
                  {time}
                </div>
              ))}
            </div>
          </div>

          {/* Section des plages à combler */}
          <DroppableRow
            id="unassigned"
            className="dv-employee-row dv-unassigned-row"
          >
            <div className="dv-employee-info">
              <div className="dv-employee-avatar dv-unassigned-avatar">
                <span className="dv-avatar-initials">?</span>
              </div>
              <div className="dv-employee-details">
                <div className="dv-employee-name dv-unassigned-name">
                  Plages à combler
                </div>
              </div>
            </div>
            <div className="dv-timeline-container">
              <div className="dv-timeline-background">
                {dayTimeline.map((_, index) => (
                  <div key={index} className="dv-timeline-slot"></div>
                ))}
              </div>
              <div className="dv-appointments-timeline">
                {unassignedAppointments.map((appointment) => {
                  const position = getAppointmentPosition(appointment);
                  return (
                    <SortableAppointment
                      key={appointment.id}
                      appointment={appointment}
                      position={position}
                      getStatusClass={(status) => `dv-unassigned ${getStatusClass(status)}`}
                      formatTime={formatTime}
                      handleAppointmentClick={handleAppointmentClick}
                      handleAppointmentDoubleClick={handleAppointmentDoubleClick}
                      canEditAppointments={canEditAppointments}
                      isMobile={isMobile}
                    />
                  );
                })}
              </div>
            </div>
          </DroppableRow>

          {/* Lignes pour les employés */}
          {visibleEmployees.map(employee => {
            const employeeAppointments = getEmployeeAppointments(employee.userId);

            return (
              <DroppableRow
                key={employee.userId}
                id={employee.userId}
                className={`dv-employee-row ${isEmployeeOnLeave(employee.userId) ? 'dv-on-leave' : ''}`}
              >
                <div className="dv-employee-info">
                  <div className="dv-employee-avatar" style={{ backgroundColor: employee.color }}>
                    {employee.photoURL ? (
                      <img
                        src={employee.photoURL}
                        alt={employee.name || `${employee.firstName} ${employee.lastName}`}
                        className="dv-avatar-image"
                      />
                    ) : (
                      <span className="dv-avatar-initials">
                        {getInitials(employee.name || `${employee.firstName} ${employee.lastName}`)}
                      </span>
                    )}
                  </div>
                  <div className="dv-employee-details">
                    <div className="dv-employee-name">
                      {employee.userId === currentUser.userId ? "Mon planning" : (employee.name || `${employee.firstName} ${employee.lastName}`)}
                    </div>
                    <div className="dv-employee-role">
                      <span
                        className="dv-role-badge"
                        style={{
                          backgroundColor: employee.roleColor,
                          color: getContrastColor(employee.roleColor)
                        }}
                      >
                        {employee.roleName}
                      </span>
                    </div>
                    {isEmployeeOnLeave(employee.userId) && (
                      <div className="dv-leave-indicator">
                        <Clock className="dv-leave-icon" size={14} />
                        <span>En congé</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="dv-timeline-container">
                  <div className="dv-timeline-background">
                    {dayTimeline.map((_, index) => (
                      <div key={index} className="dv-timeline-slot"></div>
                    ))}
                  </div>
                  {isEmployeeOnLeave(employee.userId) ? (
                    <div className="dv-leave-block">
                      <span className="dv-leave-text">En congé aujourd'hui</span>
                    </div>
                  ) : (
                    <div className="dv-appointments-timeline">
                      {employeeAppointments.map((appointment) => {
                        const position = getAppointmentPosition(appointment);
                        return (
                          <SortableAppointment
                            key={appointment.id}
                            appointment={appointment}
                            position={position}
                            getStatusClass={getStatusClass}
                            formatTime={formatTime}
                            handleAppointmentClick={handleAppointmentClick}
                            handleAppointmentDoubleClick={handleAppointmentDoubleClick}
                            canEditAppointments={canEditAppointments}
                            isMobile={isMobile}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </DroppableRow>
            );
          })}

          {/* Modales identiques à DayViewMobile */}
          {showBookingModal && selectedUnassignedAppointment && (
            <EnhancedModal onClose={() => setShowBookingModal(false)} size="md">
              <ModalHeader>Réserver ce rendez-vous</ModalHeader>
              <ModalBody>
                <div className="space-y-3">
                  <p className="text-gray-600 dark:text-gray-300">
                    Voulez-vous réserver ce rendez-vous ?
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Bénéficiaire :</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.clientName || "Bénéficiaire non spécifié"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.date.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedUnassignedAppointment.startTime} - {selectedUnassignedAppointment.endTime}</span>
                    </div>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <ModalButton variant="secondary" onClick={() => setShowBookingModal(false)}>
                  Annuler
                </ModalButton>
                <ModalButton variant="primary" onClick={handleBookAppointment}>
                  Réserver
                </ModalButton>
              </ModalFooter>
            </EnhancedModal>
          )}

          {showEditModal && selectedEditAppointment && (
            <EnhancedEditAppointmentModal
              appointment={selectedEditAppointment}
              employees={employees}
              clients={clients}
              onSave={handleUpdateAppointment}
              onClose={() => setShowEditModal(false)}
              onDelete={handleDeleteAppointment}
              canDelete={true}
            />
          )}

          {showDetailsModal && selectedDetailAppointment && (
            <EnhancedAppointmentDetailsModal
              appointment={selectedDetailAppointment}
              onClose={() => setShowDetailsModal(false)}
              onUpdate={() => {
                setRefreshTrigger(prev => prev + 1);
              }}
              onEdit={() => {
                setShowDetailsModal(false);
                setSelectedEditAppointment(selectedDetailAppointment);
                setShowEditModal(true);
              }}
              onDelete={handleDeleteAppointment}
              canEdit={
                userPermissions.manage ||
                userPermissions.write ||
                (selectedDetailAppointment.employeeId === currentUser.userId)
              }
              db={db}
            />
          )}

          {/* Modal d'information pour les rendez-vous en attente de validation */}
          {showInfoModal && selectedInfoAppointment && (
            <EnhancedModal onClose={() => setShowInfoModal(false)} size="md">
              <ModalHeader>Détails du rendez-vous</ModalHeader>
              <ModalBody>
                <div className="space-y-3">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Bénéficiaire :</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedInfoAppointment.clientName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedInfoAppointment.date.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                      <span className="text-gray-900 dark:text-gray-100">{selectedInfoAppointment.startTime} - {selectedInfoAppointment.endTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Employé :</span>
                      <span className="text-gray-900 dark:text-gray-100">{getEmployeeName(selectedInfoAppointment.employeeId)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Statut :</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedInfoAppointment.status === 'reserve'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                        {selectedInfoAppointment.status === 'reserve' ? 'En attente de validation' : selectedInfoAppointment.status}
                      </span>
                    </div>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                {/* Boutons conditionnels selon les permissions */}
                {selectedInfoAppointment.status === 'reserve' && (
                  <>
                    {/* Boutons pour les utilisateurs avec droits d'écriture */}
                    {userPermissions.write && (
                      <>
                        <ModalButton variant="success" onClick={handleApproveAppointment}>
                          Approuver
                        </ModalButton>
                        <ModalButton variant="danger" onClick={handleRejectAppointment}>
                          Refuser
                        </ModalButton>
                      </>
                    )}
                    {/* Bouton d'annulation pour l'utilisateur qui a réservé */}
                    {selectedInfoAppointment.employeeId === currentUser.userId && (
                      <ModalButton variant="warning" onClick={handleCancelReservation}>
                        Annuler
                      </ModalButton>
                    )}
                  </>
                )}
                <ModalButton variant="secondary" onClick={() => setShowInfoModal(false)}>
                  Fermer
                </ModalButton>
              </ModalFooter>
            </EnhancedModal>
          )}

          {/* Modal de confirmation pour le déplacement des rendez-vous */}
          {showMoveConfirmationModal && pendingMove && (
            <EnhancedModal onClose={cancelMoveAppointment} size="md">
              <ModalHeader>Confirmer le déplacement</ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-300">
                    Êtes-vous sûr de vouloir déplacer ce rendez-vous ?
                  </p>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Bénéficiaire :</span>
                      <span className="text-gray-900 dark:text-gray-100">{pendingMove.appointment.clientName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Date :</span>
                      <span className="text-gray-900 dark:text-gray-100">{pendingMove.appointment.date.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Heure :</span>
                      <span className="text-gray-900 dark:text-gray-100">{pendingMove.appointment.startTime} - {pendingMove.appointment.endTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Destination :</span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {pendingMove.destinationName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Nouveau statut :</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${pendingMove.newStatus === 'libre'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                        {pendingMove.newStatus === 'libre' ? 'Libre' : 'Assigné'}
                      </span>
                    </div>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <ModalButton variant="secondary" onClick={cancelMoveAppointment}>
                  Annuler
                </ModalButton>
                <ModalButton variant="primary" onClick={confirmMoveAppointment}>
                  Confirmer le déplacement
                </ModalButton>
              </ModalFooter>
            </EnhancedModal>
          )}

        </div>
      </SortableContext>

      {/* Modal pour les modifications de rendez-vous récurrents */}
      <RecurrenceMoveModal
        isOpen={showRecurrenceUpdateModal}
        onClose={() => {
          setShowRecurrenceUpdateModal(false);
          setPendingUpdateData(null);
        }}
        onConfirm={(scope) => {
          if (pendingUpdateData) {
            handleUpdateAppointment(pendingUpdateData, scope);
          }
        }}
        appointment={pendingUpdateData}
        actionType="update"
      />

      {/* Modal pour les suppressions de rendez-vous récurrents */}
      <RecurrenceMoveModal
        isOpen={showRecurrenceDeleteModal}
        onClose={() => {
          setShowRecurrenceDeleteModal(false);
          setPendingDeleteData(null);
        }}
        onConfirm={(scope) => {
          if (pendingDeleteData) {
            handleDeleteAppointment(pendingDeleteData.id, scope);
          }
        }}
        appointment={pendingDeleteData}
        actionType="delete"
      />

      {/* DragOverlay pour un meilleur feedback visuel */}
      <DragOverlay>
        {activeAppointment ? (
          <div
            className="dv-timeline-appointment dv-drag-overlay"
            style={{
              width: '200px',
              zIndex: 9999,
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
              opacity: 0.9,
              cursor: 'grabbing',
              transform: 'rotate(-2deg)',
            }}
          >
            <div className="dv-appointment-content">
              <div className="dv-appointment-time">
                {`${formatTime(activeAppointment.startTime)} - ${formatTime(activeAppointment.endTime)}`}
              </div>
              <div className="dv-appointment-details">
                {activeAppointment.clientName || 'Bénéficiaire non spécifié'}
              </div>
              {activeAppointment.notes && activeAppointment.notes.content && (
                <div className="dv-notes-indicator">
                  📝
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DayView;
